/**
 * Purpose: Provide a stable zero-opacity z-anchor layer and apply its beforeId to child layer components.
 * Assumptions: MapLibre layer ordering is stabilized by anchoring dynamic overlays to fixed beforeId targets,
 *   and this component intentionally overrides child `beforeId` props so all children render against the same anchor.
 * External refs: react-map-gl/maplibre Layer API and React cloneElement/Children utilities.
 */
import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import { Layer } from "react-map-gl/maplibre";

type ZLayerProps = {
  id: string;
  beforeId?: string;
  children?: ReactNode;
};

export function ZLayer({ id, beforeId, children }: ZLayerProps) {
  const layeredChildren = Children.map(children, (child) => {
    if (!isValidElement<{ beforeId?: string }>(child)) {
      return child;
    }

    return cloneElement(child, { beforeId: id });
  });

  return (
    <>
      <Layer
        id={id}
        type="background"
        beforeId={beforeId}
        paint={{ "background-opacity": 0 }}
      />
      {layeredChildren}
    </>
  );
}
